import {
  ConfirmDialog,
  Severity,
  SnackbarMessageProps,
  snackbarStore,
} from "@knowfully/ui.common";
import { FORM_ERROR } from "final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import useGlobalContext from "hooks/globalData";
import authStore from "stores/authStore";
import contextStore from "stores/contextStore";
import translateError from "utils/translateError";
import { suppressPendingInvitationsBanner } from "utils/pendingInvitationsBannerStorage";

type ResendAllInvitationsDialogProps = {
  open: boolean;
  pendingUserCount: number;
  close: () => void;
};

export default function ResendAllInvitationsDialog({
  open,
  pendingUserCount,
  close,
}: Readonly<ResendAllInvitationsDialogProps>) {
  const { formatMessage: t } = useIntl();
  const { organizationUserService } = useGlobalContext();
  const { organization } = contextStore.useState();
  const adminUserId = authStore.useState((s) => s.id);
  const queryClient = useQueryClient();

  const submit = async () => {
    try {
      const response = await organizationUserService.resendAllInvitations();
      const enqueuedCount = response?.data?.pendingUserCount ?? 0;

      if (enqueuedCount > 0 && adminUserId && organization?.id) {
        suppressPendingInvitationsBanner(adminUserId, organization.id);
      }

      snackbarStore.update((s) => {
        s.snackbarMessage = new SnackbarMessageProps(
          enqueuedCount > 0
            ? t({ id: "invitationsResentSuccess" }, { usersCount: enqueuedCount })
            : t({ id: "noPendingInvitationsRemain" }),
          enqueuedCount > 0 ? Severity.Success : Severity.Info
        );
      });

      queryClient.invalidateQueries(["organizationUserService.getUsers"]);
      queryClient.invalidateQueries(["organizationUserService.getPendingInvitations"]);
      close();
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const content = (
    <T
      id="resendPendingInvitationsDescription"
      values={{ usersCount: pendingUserCount }}
    />
  );

  return (
    <ConfirmDialog
      title={t({ id: "resendPendingInvitationsTitle" })}
      confirmLabel={t(
        { id: "resendCountInvitations" },
        { usersCount: pendingUserCount }
      )}
      content={content}
      open={open}
      onSubmit={submit}
      close={close}
    />
  );
}
